import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import enTranslations from '../i18n/locales/en/common.json';
import esTranslations from '../i18n/locales/es/common.json';

interface LanguageContextType {
  language: 'en' | 'es';
  setLanguage: (lang: 'en' | 'es') => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  const translations = language === 'es' ? esTranslations : enTranslations;

  // Listen for language changes from Header or other components
  useEffect(() => {
    const handleLanguageChange = (e: CustomEvent) => {
      setLanguageState(e.detail);
    };

    window.addEventListener('languageChange' as any, handleLanguageChange);

    return () => {
      window.removeEventListener('languageChange' as any, handleLanguageChange);
    };
  }, []);

  const setLanguage = (lang: 'en' | 'es') => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    // Dispatch custom event for components that listen to languageChange
    window.dispatchEvent(new CustomEvent('languageChange', { detail: lang }));
  };

  const t = (key: string): string => {
    return (translations as any)[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};